import { memo, useContext } from "react";
import { buildPath, MimicField, MimicFormik, ModalProps, useRefetchQuery, UserSettingContext } from "@tecorien/mimic";
import { AvailableFontsArray, prettifySignature, SignatureSelector } from "components/Signature";
import { AresModal } from "components/styled/AresModal";
import { GetUserSettingsDocument, useUpdateMySignatureMutation } from "generated/graphql";
import { useKeycloak } from "keycloak";
import { SignatureSettings } from "types";
import { useSimpleOnSubmit } from "utils/form";
import { serializeEntity } from "utils/serializer";

export type MySignatureModalProps = ModalProps & {}

type MySignatureDtoInput = { signature?: SignatureSettings | null };

export const MySignatureModal = memo<MySignatureModalProps>(({ ...modalProps }) => {
  const { tokenData } = useKeycloak();
  const { settings } = useContext(UserSettingContext);
  const [ update ] = useUpdateMySignatureMutation();
  const refetchQuery = useRefetchQuery(GetUserSettingsDocument);
  const signature = (!!settings.signature && !!AvailableFontsArray.find(x => x === settings.signature?.font))
    ? settings.signature
    : null;

  return (
    <MimicFormik
      enableReinitialize
      initialValues={{ signature } as MySignatureDtoInput}
      onSubmit={useSimpleOnSubmit({
        submit: async (values, formikHelpers, setResultsToForm) => {
          const { errors, data } = await update({ variables: { values: serializeEntity(values) } });
          if (data) {
            setResultsToForm(data.me?.success, data.me?.data, errors);
          } else {
            setResultsToForm(false, null, errors);
          }
        },
        onSuccess: async () => {
          refetchQuery();
          modalProps.onRequestClose?.(undefined as any);
        },
      })}
    >
      {
        props =>
          <AresModal
            {...modalProps}
            scrollable={false}
            onRequestClose={(e) => {
              if (props.isSubmitting) {
                return;
              }
              modalProps.onRequestClose?.(e);
              props.resetForm();
            }}
            onSubmit={props.handleSubmit as any}
            cancelButtonDisabled={props.isSubmitting || props.isValidating}
            submitButtonDisabled={props.isSubmitting || props.isValidating}
            title="User signature"
          >
            <MimicField
              name={buildPath<MySignatureDtoInput>()("signature")}
              component={SignatureSelector}
              optionText={prettifySignature(`${tokenData?.firstName || ""} ${tokenData?.lastName || ""}`)}
              formatMimic={(value: SignatureSettings | null) => value?.font}
              normalizeMimic={value => value ? ({ font: value } as SignatureSettings) : null}
            />
          </AresModal>
      }
    </MimicFormik>
  );
});

export default MySignatureModal;